/* Atomz rail - what is open in the left filter column, and what is worth showing.
 *
 * THE PROBLEM: the API returns a filter group per attribute the current node
 * supports, and inside a leaf that is a lot of them. At Apparel & Accessories >
 * Shoes > Sneakers this store returns 22, every one with values, against eight
 * products. atomz-refine.css section 7 expands all of them, which was a fair
 * call when far fewer came back and is about 4,100px of rail now.
 *
 * TWO RULES, both about presentation only. Neither reorders anything and
 * neither decides which filters exist: group order and the enabled set come
 * from the API and the Atomz admin.
 *
 *   OPEN WHAT IS ACTIVE. A group holding a selection is open, everything else
 *   is closed. The shopper's own choices decide what the rail shows, and a
 *   manual toggle sticks for the session. Nothing is opened by a rule that
 *   just picks a number of groups off the top.
 *
 *   HIDE WHAT CANNOT NARROW, AT EVERY WIDTH. A group where every value's count
 *   equals the result total cannot change the result set: clicking any of it
 *   returns the same products. Six of this node's 22 are that - productType,
 *   closure type, occasion style, outsole material, pronation support, toe
 *   style - each a single value covering all eight. That is length with nothing
 *   behind it, and it costs more on a phone than in the rail, because in the
 *   chip bar each one takes its own row.
 *
 * WHY IT TAKES THE CLICK: the app binds its own handler to each filter button
 * and that handler strips `.active` from every other group, which is right for
 * a row of dropdowns and wrong for a column - the group holding your selection
 * would close the moment you opened another. A capture-phase listener on the
 * document runs before a handler bound to the target and can stop the event
 * reaching it, so in the left rail the open state is this file's and in every
 * other layout the app's handler runs untouched.
 *
 * FAILURE MODE: nothing here is destructive. Without the body class the CSS
 * falls back to atomz-refine.css, which expands everything, so a change in the
 * app's markup costs a long rail rather than a broken one.
 */
(function () {
  'use strict';

  if (!/\/search/.test(window.location.pathname)) return;

  var MIN_WIDTH = 990;
  var KEY = 'atomz.rail.open';
  var DEBOUNCE = 60;

  /* Groups the shopper has opened or closed by hand, which outrank the
     active-selection rule until the tab is gone. */
  var manual = read();

  function read() {
    try { return JSON.parse(sessionStorage.getItem(KEY)) || {}; }
    catch (e) { return {}; }
  }

  function write() {
    try { sessionStorage.setItem(KEY, JSON.stringify(manual)); } catch (e) {}
  }

  /* The open/closed state is a column idea. In the chip bar every group is
     already closed and the header IS the control, so there is nothing to take
     over. */
  function collapsible() {
    return document.body.classList.contains('az-filters-left')
      && window.innerWidth >= MIN_WIDTH;
  }

  function groups() {
    return Array.prototype.slice.call(
      document.querySelectorAll('.az-filters-container .az-filter')
    );
  }

  function idOf(group) {
    var btn = group.querySelector('.az-filter-button');
    return (btn && btn.id) || '';
  }

  function hasSelection(group) {
    return !!group.querySelector('.az-filter-item-value.selected');
  }

  /* The app writes the count into .az-total-results; the raw response is the
     more reliable read when it is there. */
  function total() {
    var g = document.querySelector('atomz-product-grid');
    var p = g && g.aiResponse && g.aiResponse.products;
    var n = p && p['@odata.count'];
    if (typeof n === 'number' && n > 0) return n;

    var el = document.querySelector('.az-total-results span');
    n = el && parseInt(el.textContent, 10);
    return (typeof n === 'number' && n > 0) ? n : 0;
  }

  /* Dead only when every value covers the whole result set. One value at the
     total is the common case, but two values both at the total are equally
     useless. A missing or unparseable count means no verdict, so the group
     stays: a group wrongly hidden is worse than a group wrongly shown. */
  function cannotNarrow(group, n) {
    if (!n) return false;
    var rows = group.querySelectorAll('.az-filter-item-value');
    if (!rows.length) return false;

    for (var i = 0; i < rows.length; i++) {
      var span = rows[i].querySelector('span');
      if (!span) return false;
      var c = parseInt(String(span.textContent).replace(/[^\d]/g, ''), 10);
      if (isNaN(c) || c !== n) return false;
    }
    return true;
  }


  /*
   * PRESENTATION FROM THE VALUES, NOT FROM THE ATTRIBUTE NAME.
   *
   * The CSS used to key the size keypad and the colour swatch off a list of
   * attribute ids (attr_shoe_size, attr_size, attr_ring_size, attr_color,
   * color). That list was written from a footwear catalogue, so a bathroom
   * store got plain rows for finishes and widths while carrying dead selectors
   * for shoe sizes, and any attribute a merchant added was invisible to it.
   *
   * These two classes are decided by what is in the group. A merchant enables
   * a facet in the Atomz admin and it gets the right treatment without anyone
   * naming it here.
   */
  var SWATCH_VALUES = {};
  ['white','black','matt black','grey','gray','anthracite','chrome',
   'brushed brass','brushed nickel','gold','copper','bronze','silver',
   'cream','beige','oak','walnut','red','blue','green','brown']
    .forEach(function (v) { SWATCH_VALUES[v] = true; });

  /* Short enough that a grid reads better than a column. 1700mm, 8.5, XL and
     600 all qualify; "Brushed Brass" and a brand name do not. */
  var CHIP_MAX = 8;

  /* A range is not a chip. "50-100" clears CHIP_MAX on length alone, so a price
     group would come out as a keypad, which is wrong twice over: the values are
     spans rather than tokens, and on a phone the row treatment at 989px turns
     them into a cramped horizontal strip. Anything holding a numeric range or a
     currency mark stays a column. */
  var RANGE = /\d\s*[-\u2013\u2014]\s*\d|[$£€¥]|\+$/;

  function valuesOf(group) {
    return Array.prototype.slice.call(
      group.querySelectorAll('.az-filter-item-value')
    ).map(function (el) {
      var raw = el.getAttribute('data-value');
      if (raw === null) {
        /* The count sits in a trailing span; the label is what is left. */
        var span = el.querySelector('span');
        raw = span ? el.textContent.replace(span.textContent, '') : el.textContent;
      }
      return String(raw).trim();
    }).filter(function (v) { return v.length > 0; });
  }

  function classify(group) {
    var box = group.querySelector('.az-filter-values');
    if (!box) return;
    var vals = valuesOf(group);
    if (!vals.length) {
      box.classList.remove('az-chips', 'az-swatches');
      return;
    }

    var swatch = 0;
    var chip = 0;
    var ranged = false;
    vals.forEach(function (v) {
      if (SWATCH_VALUES[v.toLowerCase()]) swatch++;
      if (v.length <= CHIP_MAX) chip++;
      if (RANGE.test(v)) ranged = true;
    });

    /* A majority, not all of them. One "Multicolour" among ten finishes should
       not cost the other nine their swatch. */
    var isSwatch = swatch * 2 > vals.length;
    box.classList.toggle('az-swatches', isSwatch);
    /* Swatches win. A group of short colour names is a swatch list, not a
       keypad, and drawing both puts a chip border around every swatch. */
    box.classList.toggle('az-chips', !isSwatch && !ranged && chip === vals.length);
  }


  /*
   * GROUP ORDER IS THE ADMIN'S, AND IT STAYS PUT.
   *
   * The app's reorderFilterGroups() floats every group holding a selection to
   * the top of the wrapper by appendChild, then sets scrollLeft = 0. In the
   * horizontal bar that is right: the bar scrolls, and bringing the active
   * group into view is the point. In the left column it means the group you
   * just clicked leaps to the top of the rail and everything else drops a
   * place, under the cursor, on every click.
   *
   * The order the merchant configured is on the app block as
   * data-active-filters, and it matches the server-rendered order exactly, so
   * pinning `order` from it makes the DOM shuffle visually inert without
   * fighting the app for the DOM. Only in the left column at desktop; the bar
   * keeps the app's behaviour.
   */
  var adminOrder = null;

  function adminIndex(id) {
    if (adminOrder === null) {
      adminOrder = {};
      var block = document.querySelector('atomz-product-grid[data-active-filters]');
      var list = block ? (block.getAttribute('data-active-filters') || '').split(',') : [];
      list.forEach(function (name, i) {
        name = name.trim();
        if (name) adminOrder[name] = i + 1;
      });
    }
    return adminOrder[id];
  }

  function pinOrder(all, on) {
    all.forEach(function (g) {
      if (!on) { g.style.order = ''; return; }
      var box = g.querySelector('.az-filter-values');
      var id = (box && box.getAttribute('data-id')) || idOf(g);
      var n = id ? adminIndex(id) : null;
      g.style.order = n ? String(n) : '';
    });
  }

  function sync() {
    var n = total();
    var all = groups();

    /* A group that cannot narrow is dead in every layout. It was gated to the
       desktop rail at first, which meant the six useless groups were hidden on
       the widest screen and shown on the narrowest - exactly backwards, since
       a phone is where each one costs a whole row of the chip bar. */
    all.forEach(function (g) {
      g.classList.toggle('az-dead', cannotNarrow(g, n));
      /* Presentation only. It must never be able to stop the count below,
         which decides whether the rail keeps its column. */
      try { classify(g); } catch (e) { /* leave the group unclassified */ }
    });

    /*
     * IS THERE A RAIL AT ALL?
     *
     * Counted here rather than matched in CSS. Two :has() chains were trying
     * to agree on this and could not: atomz-empty.css hid the container on
     * fewer than two live groups, while the collapse rule additionally
     * refused to fire whenever #az-tax existed, so that the taxonomy group
     * alone could still hold the column. But #az-tax lives inside the
     * container, so a hidden container hid it too, and the page was left with
     * an invisible rail and a 264px gutter still reserved beside the products.
     *
     * Live means enabled by the app and not stood down above. The taxonomy
     * group counts as a rail in its own right.
     */
    var live = 0;
    all.forEach(function (g) {
      if (g.classList.contains('enabled') && !g.classList.contains('az-dead')) live++;
    });
    var hasTax = !!document.getElementById('az-tax');
    document.body.classList.toggle('az-rail-empty', !hasTax && live < 2);

    pinOrder(all, collapsible());

    if (!collapsible()) {
      document.body.classList.remove('az-rail-collapsible');
      all.forEach(function (g) { g.classList.remove('az-open'); });
      return;
    }

    document.body.classList.add('az-rail-collapsible');

    all.forEach(function (g) {
      var id = idOf(g);
      if (!id) return;
      g.classList.toggle(
        'az-open',
        Object.prototype.hasOwnProperty.call(manual, id) ? manual[id] : hasSelection(g)
      );
    });
  }

  /* Capture phase, so this runs before the handler the app bound to the button
     and can keep the event from reaching it. */
  document.addEventListener('click', function (e) {
    if (!collapsible()) return;

    var btn = e.target && e.target.closest
      && e.target.closest('.az-filters-container .az-filter-button');
    if (!btn) return;

    var group = btn.closest('.az-filter');
    var id = idOf(group);
    if (!group || !id) return;

    e.stopPropagation();
    e.preventDefault();

    var open = !group.classList.contains('az-open');
    manual[id] = open;
    write();
    group.classList.toggle('az-open', open);
  }, true);

  var timer;
  function schedule() {
    window.clearTimeout(timer);
    timer = window.setTimeout(sync, DEBOUNCE);
  }

  function watch() {
    var target = document.querySelector('.az-filters-container');
    if (!target) return false;
    new MutationObserver(schedule).observe(target, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class']
    });
    schedule();
    return true;
  }

  if (!watch()) {
    var boot = new MutationObserver(function () { if (watch()) boot.disconnect(); });
    boot.observe(document.documentElement, { subtree: true, childList: true });
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(sync, 150);
  });
})();
